import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Upload, LogOut, FileSpreadsheet, Eye, EyeOff, RefreshCw, CheckSquare, Search } from 'lucide-react';
import StockTable from './StockTable';
import ToDo from './ToDo';
import ChatWindow from './ChatWindow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SRHDashboard = ({ user, onLogout }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [showCost, setShowCost] = useState(false);
  const [viewMode, setViewMode] = useState('upload'); // 'upload' or 'view'
  const [searchTerm, setSearchTerm] = useState('');

  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API}/stock/srh`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setStocks(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch stock data');
    }
  };

  useEffect(() => {
    if (viewMode === 'view') {
      fetchStocks();
    }
  }, [viewMode]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      
      if (validExtensions.includes(fileExtension)) {
        setFile(selectedFile);
        toast.success(`File "${selectedFile.name}" selected`);
      } else {
        toast.error('Please select a valid file (.xlsx, .xls, or .csv)');
        e.target.value = null;
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/srh`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      toast.success(response.data.message);
      setFile(null);
      document.getElementById('file-input').value = null;
      
      // Switch to view mode after upload
      setViewMode('view');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    { key: 'product_name', label: 'Product Name', sortable: true, type: 'text' },
    { key: 'packing', label: 'Packing', sortable: true, type: 'text' },
    { key: 'available_qty', label: 'Available Qty', sortable: true, type: 'number' },
    { key: 'sale', label: 'Sale', sortable: true, type: 'number' },
    { key: 'cost', label: 'Cost @ SRH', sortable: true, type: 'number', hidden: !showCost },
    { key: 'expiry_date', label: 'Expiry', sortable: true, type: 'text' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-md">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Stock Management</h1>
              <p className="text-xs text-slate-500">SRH Portal</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex bg-slate-100 rounded-lg p-1">
              <Button
                data-testid="upload-tab"
                onClick={() => setViewMode('upload')}
                variant={viewMode === 'upload' ? 'default' : 'ghost'}
                size="sm"
                className={viewMode === 'upload' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <Upload className="w-4 h-4 mr-1.5" />
                Upload
              </Button>
              <Button
                data-testid="view-tab"
                onClick={() => setViewMode('view')}
                variant={viewMode === 'view' ? 'default' : 'ghost'}
                size="sm"
                className={viewMode === 'view' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                View Sheet
              </Button>
              <Button
                onClick={() => setViewMode('todo')}
                variant={viewMode === 'todo' ? 'default' : 'ghost'}
                size="sm"
                className={viewMode === 'todo' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <CheckSquare className="w-4 h-4 mr-1.5" />
                To-Do
              </Button>
            </div>
            <span className="text-sm font-semibold text-slate-700" style={{ fontSize: '15px' }}>{user.username}</span>
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="border-slate-300 hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {viewMode === 'upload' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Upload Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Stock Sheet</h2>
              <p className="text-blue-100 mt-1">Upload your inventory file with product details</p>
            </div>

            <div className="p-8">
              {/* File Format Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Required Column Headers
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Product Name</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Packing</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Available Qty</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Sale</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Cost (Optional)</span>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-3">Supported formats: .xlsx, .xls, .csv</p>
              </div>

              {/* Download Template Button */}
              <div className="mb-6">
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/template/download/srh`, {
                        headers: { 'Authorization': `Bearer ${user.token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'SRH_Stock_Upload_Template.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Template downloaded successfully');
                    } catch (error) {
                      toast.error('Failed to download template');
                    }
                  }}
                  variant="outline"
                  className="w-full h-10 border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Sample Excel Template
                </Button>
              </div>

              {/* Upload Controls */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="file-input" className="block text-sm font-medium text-slate-700 mb-2">
                    Select File
                  </label>
                  <input
                    id="file-input"
                    data-testid="file-input"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-slate-600 file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer cursor-pointer border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {file && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileSpreadsheet className="w-10 h-10 text-blue-600 mr-3" />
                      <div>
                        <p className="font-medium text-slate-800">{file.name}</p>
                        <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  data-testid="upload-button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Upload Stock Sheet
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : viewMode === 'todo' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>To-Do List</h2>
              <p className="text-slate-300 mt-1">Manage tasks and assignments</p>
            </div>
            <div className="p-6">
              <ToDo user={user} />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* View Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Your Stock Sheet</h2>
                <p className="text-blue-100 mt-1">View and analyze your uploaded inventory</p>
              </div>
              <div className="flex space-x-3">
                <Button
                  data-testid="toggle-cost-button"
                  onClick={() => setShowCost(!showCost)}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  size="sm"
                >
                  {showCost ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  {showCost ? 'Hide' : 'Show'} Cost
                </Button>
                <Button
                  data-testid="refresh-stocks-button"
                  onClick={fetchStocks}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Refresh
                </Button>
              </div>
            </div>

            <div className="px-8 py-4 border-b border-slate-200">
              <Input
                icon={<Search className="w-4 h-4" />}
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>

            <StockTable data={stocks} columns={columns} searchTerm={searchTerm} />
          </div>
        )}
      </main>

      {/* Chat Window */}
      <ChatWindow user={user} />
    </div>
  );
};

export default SRHDashboard;
